<script setup lang="ts">
import { calculateValidity, stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  approveRequisitionCancellationAction,
  getRequisitionCancellationsAction,
  rejectRequisitionCancellationAction,
} from '@/api/functions/requisitions/cancellation-decision.action'
import { deleteRequisitionAction } from '@/api/functions/requisitions/delete-requisition-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { elapsedSince } from '@/utils/elapsed-since.util'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'

const props = defineProps({
  request: { type: Object },
  cancellationReview: { type: Boolean, default: false },
})

const emit = defineEmits(['deleted', 'decided'])

const toast = useToast()
const router = useRouter()
const route = useRoute()
const showDeleteModal = ref(false)
type ModalMode = 'delete' | 'approve' | 'reject'
const modalMode = ref<ModalMode>('delete')

const cardType = computed(() => {
  return props?.request?.type === 'BUY' ? true : false
})

const requestId = computed(() => props.request?.id)

const hideButtonOnPages = ['submit-offer', 'edit', 'reOffer']

const isButtonHidden = computed(() => {
  return hideButtonOnPages.some((page) => route.path.includes(page))
})

const { data: detail, refetch: refetchDetail } = useQueryApi({
  queryKey: computed(() => ['requisitionCancellations', requestId.value]),
  queryFn: () => getRequisitionCancellationsAction(requestId.value as string),
  enabled: computed(() => !!requestId.value && props.request?.cancellationRequests === undefined),
})

const pendingCancellation = computed(() => {
  const history = props.request?.cancellationRequests ?? detail.value?.data?.cancellationRequests ?? []
  return history
    .filter((r: { status: string }) => r.status === 'PENDING')
    .sort(
      (a: { createdAt: string }, b: { createdAt: string }) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0]
})
const hasPendingCancellation = computed(() => !!pendingCancellation.value)
const modalConfig = computed(
  () =>
    ({
      delete: {
        title: 'حذف درخواست',
        message: 'آیا از حذف این درخواست اطمینان دارید؟',
        confirm: 'بله حذف شود',
      },
      approve: {
        title: 'تأیید لغو درخواست',
        message:
          'با تأیید، درخواست لغو شده می‌شود و پیشنهادهای در انتظار هم لغو می‌شوند. اگر درخواست در مرحله تراکنش باشد، در صورت امکان تراکنش مرتبط هم لغو می‌شود. ادامه می‌دهید؟',
        confirm: 'بله، تأیید شود',
      },
      reject: {
        title: 'رد درخواست لغو',
        message: 'با رد، وضعیت درخواست، پیشنهادها و تراکنش مرتبط بدون تغییر می‌ماند. ادامه می‌دهید؟',
        confirm: 'بله، رد شود',
      },
    })[modalMode.value]
)

const openModal = (mode: ModalMode) => {
  modalMode.value = mode
  showDeleteModal.value = true
}
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteRequisitionAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'درخواست با موفقیت حذف شد')
    emit('deleted')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const decisionMutation = useMutationApi({
  mutationFn: (type: 'approve' | 'reject') =>
    type === 'approve'
      ? approveRequisitionCancellationAction(requestId.value as string)
      : rejectRequisitionCancellationAction(requestId.value as string),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تصمیم شما ثبت شد')
    showDeleteModal.value = false
    refetchDetail()
    emit('decided')
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
    showDeleteModal.value = false
    refetchDetail()
    emit('decided')
  },
})

const handleDeleteRequisition = () => {
  if (!requestId.value) return
  deleteMutation.mutate(requestId.value)
}

const handleConfirm = () => {
  if (modalMode.value === 'delete') return handleDeleteRequisition()
  if (!requestId.value || decisionMutation.isPending?.value) return
  decisionMutation.mutate(modalMode.value)
}

const goToRequestDetails = () => {
  if (requestId.value && props.request?.type) {
    router.push({
      path: `/requisitions/${requestId.value}`,
    })
  }
}
</script>

<template>
  <div class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">
    <ConfirmModal v-model="showDeleteModal" :title="modalConfig.title" :message="modalConfig.message"
      :confirm-text="modalConfig.confirm" cancel-text="بازگشت" @confirm="handleConfirm"
      @cancel="showDeleteModal = false" />
    <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
      <div class=" w-full lg:w-fit flex  items-center" v-show="!!requestId">
        <Button @click="goToRequestDetails" :isAdmin="true"
          class="w-1/2 lg:w-auto  rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal me-2">
          مشاهده
        </Button>

        <Button v-if="request?.status === 'PENDING' && !hasPendingCancellation" @click="openModal('delete')"
          class="bg-transparent w-1/2 lg:w-[66px] border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-5 !h-10 py-0 text-[13px] font-normal me-2">
          <span class="flex items-center justify-center gap-2 text-[#2d5396]">
            حذف
          </span>
        </Button>
        <template v-if="hasPendingCancellation">
          <Button @click="openModal('approve')" :isAdmin="true"
            class="w-1/2 lg:w-auto rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal me-2">
            تأیید لغو
          </Button>
          <Button @click="openModal('reject')"
            class="bg-transparent w-1/2 lg:w-auto border-[#ed145b] border-2 hover:bg-gray-100 rounded-[10px]! px-5 !h-10 py-0 text-[13px] font-normal me-2">
            <span class="flex items-center justify-center gap-2 text-[#ed145b]">
              رد لغو
            </span>
          </Button>
        </template>
      </div>
    </div>

    <div class="flex flex-col gap-6  text-black text-sm font-medium">
      <!-- Header -->
      <div class="flex flex-col lg:flex-row items-center justify-center gap-2 lg:justify-start">
        <div class="w-full lg:w-fit flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-6"
          :class="request?.status === 'PENDING' ? 'bg-[#fff8e7] text-[#ffba0e] ' :
            request?.status === 'IN_TRANSACTION' ? 'bg-[#d5ddea] text-[#2d5396] ' :
              request?.status === 'FINISHED' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                request?.status === 'DELETEDBYUSER' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                  request?.status === 'DELETEDBYADMIN' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                    'bg-[#fde7ef] text-[#ed145b] '">
          درخواست
          <span class="font-fa ms-1">{{ request?.code }}</span>
          <span v-if="!isButtonHidden" class="mx-2">|</span>
          <span v-if="!isButtonHidden">
            {{ request?.status === 'PENDING' ? 'درحال انتظار' :
              request?.status === 'IN_TRANSACTION' ? 'در حال انجام' :
                request?.status === 'FINISHED' ? 'پایان یافته' :
                  request?.status === 'DELETEDBYUSER' ? 'حذف شده توسط کاربر' :
                    request?.status === 'DELETEDBYADMIN' ? 'حذف شده توسط ادمین' :
                      request?.status === 'CANCELLED' ? 'لغو شده' : 'منقضی شده' }}
          </span>
        </div>

        <div v-if="hasPendingCancellation"
          class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#fff8e7] text-[#ffba0e] font-medium h-10 rounded-[10px] px-6">
          در انتظار بررسی لغو
        </div>

        <div v-if="request?.discount?.currentUser"
          class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#f3d0f2] text-[#8c228a] font-medium h-10 rounded-[10px] px-6">
          دارای تخفیف
        </div>

      </div>
      <!-- Body -->
      <div class="flex flex-col gap-2.5 text-sm text-black font-medium">
        <div class="flex items-center gap-1">
          <span>درخواست کننده:</span>
          <RouterLink v-if="request?.owner?.id" :to="`/users/${request.owner.uuid}`"
            class="text-[#2d5396] hover:underline">
            {{ request?.owner?.fullName }}
          </RouterLink>
          <span v-else>{{ request?.owner?.fullName }}</span>
          <span class="font-fa">( {{ request?.owner?.id }})</span>
        </div>
        <div class="flex items-center gap-1">
          <div v-if="request?.type === 'CHARITY'"
            class="w-fit h-6 px-1 rounded-lg bg-[#d8d8d8]  text-[15px] font-normal flex items-center justify-center gap-1.5">
            خیریه
          </div>
          <span>{{ cardType ? 'خرید' : 'فروش' }}</span>
          <span class="font-fa">{{ numberWithCommas(request?.currencyAmount) }} {{ request?.currency?.titleFa
          }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>نرخ تبدیل:</span>
          <span class="font-fa">{{ request?.exchangeRate?.toLocaleString() }} تومان</span>
        </div>
        <div class="flex items-center gap-1">
          <span>نوع انتقال:</span>
          <span>
            {{
              request?.transferMethods?.length > 0
                ? request?.transferMethods.map((method: { label: string }) => method.label).join(', ')
                : 'تعیین نشده'
            }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <span>پیشنهادهای دریافتی :</span>
          <span class="font-fa">
            {{ request?.totalOffers }}
          </span>
        </div>
      </div>
      <!-- Footer -->
      <div
        class="flex flex-col lg:flex-row lg:items-center gap-6 mt-2 lg:mt-4 lg:gap-0  pe-1 text-sm text-[#1b325a] font-normal"
        :class="request?.bankAccountCountries?.internationalBuyer ||
          request?.bankAccountCountries?.internationalPaypalBuyer ||
          request?.bankAccountCountries?.internationalRevolutBuyer ||
          request?.bankAccountCountries?.seller ? 'justify-between' : 'justify-end'">
        <!-- Country  -->

        <div v-if="request?.type === 'SELL'" class="flex items-center gap-1 text-sm font-medium text-[#000000]">
          <span v-if="request?.bankAccountCountries?.seller" class="">از</span>
          <img :src="request?.bankAccountCountries?.seller?.flagUrl" width="24" height="16" alt="flag" />
          {{ request?.bankAccountCountries?.seller?.title?.fa }}
        </div>

        <div v-if="request?.type === 'BUY'" class="flex items-center gap-1">

          <div v-if="request?.bankAccountCountries?.internationalBuyer"
            class="flex items-center gap-1 text-sm font-medium text-[#000000]">
            <img :src="request?.bankAccountCountries?.internationalBuyer?.flagUrl" width="24" height="16" alt="flag" />
            {{ request?.bankAccountCountries?.internationalBuyer?.title?.fa }}
          </div>
          <div v-if="request?.bankAccountCountries?.internationalPaypalBuyer"
            class="flex items-center gap-1 text-sm font-medium text-[#000000]">
            <img :src="request?.bankAccountCountries?.internationalPaypalBuyer?.flagUrl" width="22" height="16"
              alt="flag" />
            {{ request?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa }}
          </div>
          <div v-if="request?.bankAccountCountries?.internationalRevolutBuyer"
            class="flex items-center gap-1 text-sm font-medium text-[#000000]">
            <img :src="request?.bankAccountCountries?.internationalRevolutBuyer?.flagUrl" width="22" height="16"
              alt="flag" />
            {{ request?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa }}
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between lg:justify-end text-sm font-medium gap-4 mb-2">
          <div class="flex items-center">
            ثبت: <span dir="ltr" class="ms-1 font-fa">{{ stringToFaDateTime(request?.createdAt) }}</span>
          </div>
          <div v-if="hasPendingCancellation" class="flex items-center">
            گذشته از درخواست لغو:
            <span class="font-fa mx-1">{{ elapsedSince(pendingCancellation?.createdAt) }}</span>
          </div>
          <div v-else class="flex items-center">اعتبار:
            <span class="font-fa mx-1">
              {{ calculateValidity(request?.expiresAt) }}
            </span>
          </div>
        </div>
      </div>
      <!-- Description -->
      <div v-if="request?.description"
        class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium -mt-2">
        توضیحات: {{ request?.description }}
      </div>
    </div>
  </div>

</template>
